import { GBasePass, GetBasePassUniformParametersList, Mesh } from "./basePass";
import {
    CreateCubemapFramebuffers,
    CreateCubemapRT,
    CreateFramebufferWithAttachment,
    CubemapFaceIndexMap,
} from "./core/resourcesUtils";
import { Shader } from "./core/shaderUtils";
import { GStack, GetVec2, GetVec3, Vector3 } from "./core/types";
import { GLBindUniformBuffer, GLSetTexture, GLSetTextureCubemap, GLSetVec3 } from "./helpers/glHelper";
import { SceneBase } from "./scenes/scene";
import { DatGuiAddVec3 } from "./core/gui";
import { CommonRenderingResources } from "./shaders/shaderConfig";
import { ShaderSourceFullscreenPassVS } from "./shaders/shaderPostProcess";
import {
    GetShaderSourceComputeIrradianceCubemapPS,
    GetShaderSourceComputeIrradianceRectPS,
    GetShaderSourceComputeReflectedIrradianceCubemapPS,
    GetShaderSourceComputeSHCoefPS,
    GetShaderSourceConvertRectEnvToCubemapPS,
    GetShaderSourceFullscreenLineRenderVS,
} from "./shaders/shaderEnvProbes";

enum ECubemapFaces {
    Right = 0,
    Left,
    Down,
    Up,
    Backward,
    Forward,
}

export class RLightprobe {
    Position = GetVec3(0, 0, 0);
    IndexFlat = 0;
    IndexCoord = GetVec3(0, 0, 0);
    EnabledFaces: { [key in ECubemapFaces]: boolean } = {
        [ECubemapFaces.Left]: true,
        [ECubemapFaces.Right]: true,
        [ECubemapFaces.Up]: true,
        [ECubemapFaces.Down]: true,
        [ECubemapFaces.Forward]: true,
        [ECubemapFaces.Backward]: true,
    };
    DisableAllFaces() {
        for (let p = 0; p < 6; p++) {
            const faceIndex = p as ECubemapFaces;
            this.EnabledFaces[faceIndex] = false;
        }
    }
    EnalbeAllFaces() {
        for (let p = 0; p < 6; p++) {
            const faceIndex = p as ECubemapFaces;
            this.EnabledFaces[faceIndex] = true;
        }
    }
}

export class RLightprobesManager {
    DebugVisualizeMesh = new Mesh();

    //Intermediate Cubemap
    LightprobeCubemapTexture: WebGLTexture | null = null;
    LightprobeCubemapTextureFramebuffers: WebGLFramebuffer[] | null = null;
    DepthBufferRT: WebGLRenderbuffer | null = null;
    bUseDepthBuffer = true;

    //Global texture where all SH coefficients are stored
    TextureSHCoeff: WebGLTexture | null = null;
    TextureSHCoeffFBO: WebGLFramebuffer | null = null;

    ShaderGenerateSHCoeff: Shader | null = null;

    Lightprobes: RLightprobe[] = [];
    NumProbes = GetVec3(4, 4, 4);
    NumSHCoefficients = 12; //4 Coeff per Color channel

    ProbeRadius = 0.5;
    EnvTextureSize = 128;

    SubmitDebugUI(datGui: dat.GUI) {
        const folder = datGui.addFolder("Probes");

        let c = 0;
        for (const probe of this.Lightprobes!) {
            const name = c.toString();
            const probeFolder = folder.addFolder(name);
            DatGuiAddVec3(probeFolder, probe.Position, `Position`, -10, 10, false);
            c++;
        }
    }

    Init(gl: WebGL2RenderingContext, sceneBoxCenter: Vector3, sceneboxExtent: Vector3) {
        //Intermediate Textures
        this.LightprobeCubemapTexture = CreateCubemapRT(
            gl,
            this.EnvTextureSize,
            gl.R11F_G11F_B10F,
            gl.RGB,
            gl.HALF_FLOAT,
        );
        this.LightprobeCubemapTextureFramebuffers = CreateCubemapFramebuffers(gl, this.LightprobeCubemapTexture!);

        this.DepthBufferRT = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, this.DepthBufferRT);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, this.EnvTextureSize, this.EnvTextureSize); // Adjust width and height

        for (let p = 0; p < 6; p++) {
            const fbo = SceneBase.LightprobesManager.LightprobeCubemapTextureFramebuffers![p];
            gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
            gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.DepthBufferRT);
        }

        //SH Generation
        this.ShaderGenerateSHCoeff = new Shader(
            gl,
            GetShaderSourceFullscreenLineRenderVS(),
            GetShaderSourceComputeSHCoefPS(),
            GetBasePassUniformParametersList,
        );

        //SH Coef Texture
        {
            const size = GetVec2(this.NumSHCoefficients, this.NumProbes.x * this.NumProbes.y * this.NumProbes.z);
            const format = gl.RED;
            const internalFormat = gl.R16F;
            const type = gl.HALF_FLOAT;

            this.TextureSHCoeff = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, this.TextureSHCoeff);
            const level = 0;
            const border = 0;
            const data = null;
            gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, size.x, size.y, border, format, type, data);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

            this.TextureSHCoeffFBO = CreateFramebufferWithAttachment(gl, this.TextureSHCoeff!);
        }

        //Init Probes
        this.InitProbesPositions(sceneBoxCenter, sceneboxExtent);

        //Init Visualization Mesh
        /* if (GSettings.bDebugLightprobes) {
            const mesh = this.DebugVisualizeMesh;
            mesh.LoadMesh(gl, `assets/meshes/test/hpsphere.gltf`, 0);
            //mesh.LoadMesh(gl, `assets/meshes/test/Suzanne.gltf`);
            mesh.Scale.SetSame(0.25);
            const matName = `brushMetal`;
            //const matName = `woodTiles1`;
            mesh.Material.InitTextures(gl, matName + `DFS`, matName + `NRM`, matName + `RGH_R8`, false);
            mesh.Material.TextureScale = 2.0;
        } */
    }

    InitProbesPositions(sceneBoxCenter: Vector3, sceneboxExtent: Vector3) {
        this.Lightprobes = [];
        const probesStartPos = GStack.TempVec3;
        probesStartPos.Set3(
            sceneBoxCenter.x - sceneboxExtent.x,
            sceneBoxCenter.y - sceneboxExtent.y,
            sceneBoxCenter.z - sceneboxExtent.z,
        );
        const distanceBetweenProbes = GStack.TempVec3_2;
        distanceBetweenProbes.x = (sceneboxExtent.x * 2.0) / (this.NumProbes.x - 1);
        distanceBetweenProbes.y = (sceneboxExtent.y * 2.0) / (this.NumProbes.y - 1);
        distanceBetweenProbes.z = (sceneboxExtent.z * 2.0) / (this.NumProbes.z - 1);

        for (let iz = 0; iz < this.NumProbes.z; iz++) {
            for (let iy = 0; iy < this.NumProbes.x; iy++) {
                for (let ix = 0; ix < this.NumProbes.x; ix++) {
                    const newProbe = new RLightprobe();
                    newProbe.IndexCoord.Set3(ix, iy, iz);
                    newProbe.IndexFlat = iz * this.NumProbes.x * this.NumProbes.y + iy * this.NumProbes.x + ix;
                    newProbe.Position.x = probesStartPos.x + distanceBetweenProbes.x * ix;
                    newProbe.Position.y = probesStartPos.y + distanceBetweenProbes.y * iy;
                    newProbe.Position.z = probesStartPos.z + distanceBetweenProbes.z * iz;
                    //disable face for edge probe
                    /* if (ix == 0) {
                        newProbe.EnabledFaces[ECubemapFaces.Left] = false;
                    }
                    if (ix == this.NumProbes.x - 1) {
                        newProbe.EnabledFaces[ECubemapFaces.Right] = false;
                    }
                    if (iy == 0) {
                        newProbe.EnabledFaces[ECubemapFaces.Down] = false;
                    }
                    if (iy == this.NumProbes.y - 1) {
                        newProbe.EnabledFaces[ECubemapFaces.Up] = false;
                    }
                    if (iz == this.NumProbes.z - 1) {
                        newProbe.EnabledFaces[ECubemapFaces.Backward] = false;
                    } */
                    this.Lightprobes.push(newProbe);
                }
            }
        }
    }

    CurProbeToRenderTo = 0;

    BeginRenderToProbes(gl: WebGL2RenderingContext, curShader: Shader) {
        GBasePass.GInstance!.Begin(gl, curShader);
        gl.disable(gl.DEPTH_TEST);
        /* gl.useProgram(curShader.ShaderProgram);
        const uniformLoc = curShader.UniformParametersLocationList;
        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            uniformLoc.SceneUniformBuffer,
        ); */
    }

    UpdateProbes(gl: WebGL2RenderingContext, scene: SceneBase) {
        this.BeginRenderToProbes(gl, scene.ShaderBasePassCurScene!);
        const numProbes = this.Lightprobes!.length;
        const curProbe = this.Lightprobes![numProbes - 1 - this.CurProbeToRenderTo];
        this.UpdateProbe(gl, scene, curProbe);
        this.CurProbeToRenderTo++;
        this.CurProbeToRenderTo = this.CurProbeToRenderTo % numProbes;
    }

    RenderDebugVisualizeProbes(gl: WebGL2RenderingContext, curShader: Shader) {
        //Visualize all probes

        for (const curProbe of this.Lightprobes!) {
            this.DebugVisualizeMesh.Position = curProbe.Position;
            this.DebugVisualizeMesh.RegenerateMatrix();

            GBasePass.GInstance!.RenderMesh(gl, curShader, this.DebugVisualizeMesh, null, 2, curProbe.IndexFlat);
        }
    }

    UpdateProbe(gl: WebGL2RenderingContext, scene: SceneBase, probe: RLightprobe) {
        //Set Camera Desc
        //SceneBase.UniformBuffer.Params.CameraPosition.Set3(0.0, 0.0, this.MeshTest.Position.z);
        SceneBase.UniformBuffer.Params.CameraPosition.Set(probe.Position);
        SceneBase.UniformBuffer.Params.CameraZoom.x = 1.02;
        SceneBase.UniformBuffer.Params.ScreenRatio.x = 1.0;
        SceneBase.UploadSerializedUniformBufferToGPU(gl);

        gl.viewport(0.0, 0.0, SceneBase.LightprobesManager.EnvTextureSize, SceneBase.LightprobesManager.EnvTextureSize);
        // Enable back face culling
        gl.enable(gl.CULL_FACE);
        gl.cullFace(gl.FRONT);

        gl.clearColor(0.0, 0.0, 0.0, 0.0);

        for (let p = 0; p < 6; p++) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, SceneBase.LightprobesManager.LightprobeCubemapTextureFramebuffers![p]);

            if (SceneBase.LightprobesManager!.bUseDepthBuffer) {
                gl.clearDepth(1.0); // Set clear depth to maximum
                gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            } else {
                gl.clear(gl.COLOR_BUFFER_BIT);
            }

            const faceIndex = p as ECubemapFaces;
            if (probe.EnabledFaces[faceIndex] == true) {
                //SetUp camera
                SceneBase.CubemapFaceLookAtMap[p]();
                SceneBase.ComputeAndGetCameraViewMatrix();

                if (p == 5) {
                    /* gl.clearColor(0.0, 0.0, 0.0, 0.0);
                    gl.clear(gl.COLOR_BUFFER_BIT); */
                }

                for (const meshRef of scene.AllMeshesArr) {
                    GBasePass.GInstance!.RenderMesh(
                        gl,
                        scene.ShaderBasePassCurScene!,
                        meshRef,
                        SceneBase.TextureEnvMapWithMips!,
                        1,
                    );
                }
            }
        }

        gl.disable(gl.CULL_FACE);

        SceneBase.LightprobesManager.GenerateSHCoefficients(
            gl,
            SceneBase.LightprobesManager.LightprobeCubemapTexture!,
            probe.IndexFlat,
        );

        //Generate Irradiance for each cube face
        /* GConvolutionPass.GenerateIrradiance(
            gl,
            //SceneBase.LightprobeCubemapTexture!,
            SceneBase.LightprobesManager.LightprobeCubemapTexture!,
            SceneBase.LightprobesManager.LightprobeIrradianceCubemapTextureFramebuffers!,
            SceneBase.LightprobesManager.EnvTextureSize,
        ); */

        //Return camera transform
        SceneBase.CameraLookForward();
        SceneBase.UpdateUniformBufferParamsFromExternal();
        SceneBase.ComputeAndGetCameraViewMatrix();
        SceneBase.UploadSerializedUniformBufferToGPU(gl);
    }

    GenerateSHCoefficients(gl: WebGL2RenderingContext, srcCubemap: WebGLTexture, destProbeIndex: number) {
        const curShader = this.ShaderGenerateSHCoeff!;
        gl.viewport(0, destProbeIndex, this.NumSHCoefficients, 1);

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.TextureSHCoeffFBO);

        gl.useProgram(curShader.ShaderProgram);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        GLSetTextureCubemap(gl, curShader.UniformParametersLocationList.CubemapTexture, srcCubemap, 0);

        gl.drawArrays(gl.LINES, 0, 2);
    }
}

export class GConvolutionPass {
    static GInstance: GConvolutionPass | null = null;

    private ShaderComputeIrradianceRect: Shader;
    private ShaderComputeIrradianceCubemap: Shader;

    private ShaderComputeReflectedIrradianceCubemap: Shader;

    private ShaderConvertRectEnvToCubemap: Shader;

    constructor(gl: WebGL2RenderingContext) {
        this.ShaderComputeIrradianceRect = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceComputeIrradianceRectPS(),
            GetBasePassUniformParametersList,
        );
        this.ShaderComputeIrradianceCubemap = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceComputeIrradianceCubemapPS(),
            GetBasePassUniformParametersList,
        );
        this.ShaderComputeReflectedIrradianceCubemap = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceComputeReflectedIrradianceCubemapPS(),
            GetBasePassUniformParametersList,
        );
        this.ShaderConvertRectEnvToCubemap = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceConvertRectEnvToCubemapPS(),
            GetBasePassUniformParametersList,
        );
    }

    CopyEnvRectToCubemapFace(
        gl: WebGL2RenderingContext,
        src: WebGLTexture,
        destFBO: WebGLFramebuffer,
        destSize: number,
        normalWS: Vector3,
        tangentWS: Vector3,
    ) {
        const curShader = this.ShaderConvertRectEnvToCubemap;
        gl.viewport(0, 0, destSize, destSize);
        gl.bindFramebuffer(gl.FRAMEBUFFER, destFBO);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetVec3(gl, curShader.UniformParametersLocationList.Normal, normalWS);
        GLSetVec3(gl, curShader.UniformParametersLocationList.Tangent, tangentWS);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        GLSetTexture(gl, curShader.UniformParametersLocationList.SphericalTexture, src, 0);

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    ConvertRectnvToCubemap(gl: WebGL2RenderingContext, src: WebGLTexture, dest: WebGLFramebuffer[], destSize: number) {
        //Generate Irradiance for each cube face
        const normalWS = GStack.TempVec3;
        const tangentWS = GStack.TempVec3_2;
        //right left up down forward backward
        {
            //right
            {
                normalWS.Set3(1.0, 0.0, 0.0);
                tangentWS.Set3(0.0, 0.0, -1.0);
                GConvolutionPass.GInstance!.CopyEnvRectToCubemapFace(gl, src, dest[0], destSize, normalWS, tangentWS);
            }
            //left
            {
                normalWS.Set3(-1.0, 0.0, 0.0);
                tangentWS.Set3(0.0, 0.0, 1.0);
                GConvolutionPass.GInstance!.CopyEnvRectToCubemapFace(gl, src, dest[1], destSize, normalWS, tangentWS);
            }
            //up
            {
                normalWS.Set3(0.0, 1.0, 0.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.CopyEnvRectToCubemapFace(gl, src, dest[2], destSize, normalWS, tangentWS);
            }
            //down
            {
                normalWS.Set3(0.0, -1.0, 0.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.CopyEnvRectToCubemapFace(gl, src, dest[3], destSize, normalWS, tangentWS);
            }
            //forward
            {
                normalWS.Set3(0.0, 0.0, 1.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.CopyEnvRectToCubemapFace(gl, src, dest[4], destSize, normalWS, tangentWS);
            }
            //back
            {
                normalWS.Set3(0.0, 0.0, -1.0);
                tangentWS.Set3(-1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.CopyEnvRectToCubemapFace(gl, src, dest[5], destSize, normalWS, tangentWS);
            }
        }
    }

    GenerateReflectedIrradianceCubemapFace(
        gl: WebGL2RenderingContext,
        srcMip: WebGLTexture,
        destMipFBO: WebGLFramebuffer,
        destMipSize: number,
        normalWS: Vector3,
        tangentWS: Vector3,
        roughness: number,
        srcMipIndex: number,
    ) {
        const curShader = this.ShaderComputeReflectedIrradianceCubemap;
        gl.viewport(0, 0, destMipSize, destMipSize);
        gl.bindFramebuffer(gl.FRAMEBUFFER, destMipFBO);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetVec3(gl, curShader.UniformParametersLocationList.Normal, normalWS);
        GLSetVec3(gl, curShader.UniformParametersLocationList.Tangent, tangentWS);
        gl.uniform1f(curShader.UniformParametersLocationList.CurRoughness, roughness);
        gl.uniform1f(curShader.UniformParametersLocationList.SrcMip, srcMipIndex);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        GLSetTextureCubemap(gl, curShader.UniformParametersLocationList.CubemapTexture, srcMip, 0);

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    GenerateRelfectedIrradianceCubemap(
        gl: WebGL2RenderingContext,
        src: WebGLTexture,
        dest: WebGLTexture,
        fbo: WebGLFramebuffer[],
    ) {
        //return;
        //Generate Irradiance for each cube face
        const normalWS = GStack.TempVec3;
        const tangentWS = GStack.TempVec3_2;
        //const numMips = SceneBase.TextureEnvMapSize;
        let destMipSize = SceneBase.TextureEnvMapSize;
        const numMips = 10;
        //const numMips = Math.log2(SceneBase.TextureEnvMapSize);
        let curRoughness = 0.04;
        for (let mip = 0; mip < numMips; mip++) {
            //bind cur mip level to framebuffer
            for (let p = 0; p < 6; p++) {
                //const targetFace = CubemapFaceIndexMap[i];
                //Framebuffer
                gl.bindFramebuffer(gl.FRAMEBUFFER, fbo[p]);
                //Attach face
                gl.framebufferTexture2D(
                    gl.FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT0,
                    gl.TEXTURE_CUBE_MAP_POSITIVE_X + p,
                    dest,
                    mip,
                );
            }

            //right left up down forward backward
            //right
            {
                normalWS.Set3(1.0, 0.0, 0.0);
                tangentWS.Set3(0.0, 0.0, -1.0);
                GConvolutionPass.GInstance!.GenerateReflectedIrradianceCubemapFace(
                    gl,
                    src,
                    fbo[0],
                    destMipSize,
                    normalWS,
                    tangentWS,
                    curRoughness,
                    mip - 1,
                );
            }
            //left
            {
                normalWS.Set3(-1.0, 0.0, 0.0);
                tangentWS.Set3(0.0, 0.0, 1.0);
                GConvolutionPass.GInstance!.GenerateReflectedIrradianceCubemapFace(
                    gl,
                    src,
                    fbo[1],
                    destMipSize,
                    normalWS,
                    tangentWS,
                    curRoughness,
                    mip - 1,
                );
            }
            //up
            {
                normalWS.Set3(0.0, 1.0, 0.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateReflectedIrradianceCubemapFace(
                    gl,
                    src,
                    fbo[2],
                    destMipSize,
                    normalWS,
                    tangentWS,
                    curRoughness,
                    mip - 1,
                );
            }
            //down
            {
                normalWS.Set3(0.0, -1.0, 0.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateReflectedIrradianceCubemapFace(
                    gl,
                    src,
                    fbo[3],
                    destMipSize,
                    normalWS,
                    tangentWS,
                    curRoughness,
                    mip - 1,
                );
            }
            //forward
            {
                normalWS.Set3(0.0, 0.0, 1.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateReflectedIrradianceCubemapFace(
                    gl,
                    src,
                    fbo[4],
                    destMipSize,
                    normalWS,
                    tangentWS,
                    curRoughness,
                    mip - 1,
                );
            }
            //back
            {
                normalWS.Set3(0.0, 0.0, -1.0);
                tangentWS.Set3(-1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateReflectedIrradianceCubemapFace(
                    gl,
                    src,
                    fbo[5],
                    destMipSize,
                    normalWS,
                    tangentWS,
                    curRoughness,
                    mip - 1,
                );
            }
            curRoughness += 1 / numMips;
            destMipSize *= 0.5;
        } //for loop END

        gl.bindTexture(gl.TEXTURE_CUBE_MAP, src);
        /* gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAX_LEVEL, 10000); */
        //gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAX_LEVEL, 0);

        //gl.generateMipmap(gl.TEXTURE_CUBE_MAP);

        for (let p = 0; p < 6; p++) {
            const targetFace = CubemapFaceIndexMap[p];
            //Framebuffer
            gl.bindFramebuffer(gl.FRAMEBUFFER, fbo[p]);
            //Attach face
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, targetFace, src, 0);
        }
    }

    /* GenerateIrradianceRect(gl: WebGL2RenderingContext) {
        const curShader = this.ShaderComputeIrradianceRect;
        gl.viewport(0, 0, 512, 256);
        gl.bindFramebuffer(gl.FRAMEBUFFER, SceneBase.TextureEnvMapIrradianceFBO);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        GLSetTexture(gl, curShader.UniformParametersLocationList.SphericalTexture, SceneBase.TextureEnvMap, 0);

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    GenerateIrradianceCubeface(
        gl: WebGL2RenderingContext,
        src: WebGLTexture,
        destFBO: WebGLFramebuffer,
        destSize: number,
        normalWS: Vector3,
        tangentWS: Vector3,
    ) {
        const curShader = this.ShaderComputeIrradianceCubemap;
        gl.viewport(0, 0, destSize, destSize);
        gl.bindFramebuffer(gl.FRAMEBUFFER, destFBO);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetVec3(gl, curShader.UniformParametersLocationList.Normal, normalWS);
        GLSetVec3(gl, curShader.UniformParametersLocationList.Tangent, tangentWS);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        GLSetTextureCubemap(gl, curShader.UniformParametersLocationList.CubemapTexture, src, 0);

        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.SHCoefficientsTexture,
            SceneBase.LightprobesManager.TextureSHCoeff,
            0,
        );

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    } */

    /* static GenerateIrradiance(
        gl: WebGL2RenderingContext,
        src: WebGLTexture,
        dest: WebGLFramebuffer[],
        destSize: number,
    ) {
        //Generate Irradiance for each cube face
        const normalWS = GStack.TempVec3;
        const tangentWS = GStack.TempVec3_2;
        //right left up down forward backward
        if (1) {
            //right
            {
                normalWS.Set3(1.0, 0.0, 0.0);
                tangentWS.Set3(0.0, 0.0, -1.0);
                GConvolutionPass.GInstance!.GenerateIrradianceCubeface(gl, src, dest[0], destSize, normalWS, tangentWS);
            }
            //left
            {
                normalWS.Set3(-1.0, 0.0, 0.0);
                tangentWS.Set3(0.0, 0.0, 1.0);
                GConvolutionPass.GInstance!.GenerateIrradianceCubeface(gl, src, dest[1], destSize, normalWS, tangentWS);
            }
            //up
            {
                normalWS.Set3(0.0, 1.0, 0.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateIrradianceCubeface(gl, src, dest[2], destSize, normalWS, tangentWS);
            }
            //down
            {
                normalWS.Set3(0.0, -1.0, 0.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateIrradianceCubeface(gl, src, dest[3], destSize, normalWS, tangentWS);
            }
            //forward
            {
                normalWS.Set3(0.0, 0.0, 1.0);
                tangentWS.Set3(1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateIrradianceCubeface(gl, src, dest[4], destSize, normalWS, tangentWS);
            }
            //back
            {
                normalWS.Set3(0.0, 0.0, -1.0);
                tangentWS.Set3(-1.0, 0.0, 0.0);
                GConvolutionPass.GInstance!.GenerateIrradianceCubeface(gl, src, dest[5], destSize, normalWS, tangentWS);
            }
        }
    } */
}

export function CreateGenericEnvMap(gl: WebGL2RenderingContext) {
    SceneBase.TextureEnvMap = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, SceneBase.TextureEnvMap);
    if (SceneBase.TextureEnvMap) {
        console.log(SceneBase.TextureEnvMap);
    } else {
        console.error("Cant create cubemap");
    }

    const ctx = document.createElement("canvas").getContext("2d");

    function generateFace(
        c: CanvasRenderingContext2D,
        faceColor: string | CanvasGradient | CanvasPattern,
        textColor: string,
        text: string,
    ) {
        const { width, height } = c.canvas;
        c.fillStyle = "#000";
        //c.fillStyle = faceColor;
        c.fillRect(0, 0, width, height);
        c.font = `${width * 0.7}px sans-serif`;
        c.textAlign = "center";
        c.textBaseline = "middle";
        //c.fillStyle = textColor;
        c.fillText(text, width / 2, height / 2);
    }

    if (ctx) {
        ctx.canvas.width = 128;
        ctx.canvas.height = 128;

        const faceInfos = [
            { target: gl.TEXTURE_CUBE_MAP_POSITIVE_X, faceColor: "#F00", textColor: "#0FF", text: "+X" },
            { target: gl.TEXTURE_CUBE_MAP_NEGATIVE_X, faceColor: "#FF0", textColor: "#00F", text: "-X" },
            { target: gl.TEXTURE_CUBE_MAP_POSITIVE_Y, faceColor: "#0F0", textColor: "#F0F", text: "+Y" },
            { target: gl.TEXTURE_CUBE_MAP_NEGATIVE_Y, faceColor: "#0FF", textColor: "#F00", text: "-Y" },
            { target: gl.TEXTURE_CUBE_MAP_POSITIVE_Z, faceColor: "#00F", textColor: "#FF0", text: "+Z" },
            { target: gl.TEXTURE_CUBE_MAP_NEGATIVE_Z, faceColor: "#F0F", textColor: "#0F0", text: "-Z" },
        ];
        faceInfos.forEach((faceInfo) => {
            const { target, faceColor, textColor, text } = faceInfo;
            generateFace(ctx, faceColor, textColor, text);

            // Upload the canvas to the cubemap face.
            const level = 0;
            const internalFormat = gl.RGBA;
            const format = gl.RGBA;
            const type = gl.UNSIGNED_BYTE;
            gl.texImage2D(target, level, internalFormat, format, type, ctx.canvas);
        });
        gl.generateMipmap(gl.TEXTURE_CUBE_MAP);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    } else {
        console.error("Cant create canvas 2d");
    }
}
